import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Search, Download, Archive, FileText, Upload, CheckCircle, ShieldCheck, RefreshCw } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', slate: '#5C6B7A', amber: '#E8A020', red: '#E24B4A' };
const CATS = [['', 'All'], ['product', 'Product'], ['transaction', 'Transaction'], ['upload', 'Uploads']];
const STATUS_COLORS = { DRAFT: C.amber, APPROVED: C.green, SUPERSEDED: C.slate, WITHDRAWN: C.red };

function KPI({ label, value, color }) {
  return (
    <Card className="p-3.5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
      <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>{label}</p>
      <p className="text-xl font-bold mt-1" style={{ color }}>{value}</p>
    </Card>
  );
}

function FormsLibraryCard({ API_BASE }) {
  const CLASSES = [
    ['sme_commercial', 'SME Commercial Package'], ['public_liability', 'Public Liability'],
    ['commercial_motor', 'Commercial Motor'], ['residential_landlords', 'Residential Landlords'],
    ['commercial_landlords', 'Commercial Landlords'], ['strata', 'Strata'],
    ['professional_indemnity', 'Professional Indemnity'], ['farm', 'Farm'],
    ['home_contents', 'Home & Contents'], ['personal_motor', 'Prestige & Specialty Motor'],
    ['construction', 'Construction'], ['trade_credit', 'Trade Credit'], ['cyber', 'Cyber'],
    ['management_liability', 'Management Liability'], ['group_personal_accident', 'Group Personal Accident'],
    ['plant_equipment', 'Plant & Equipment'], ['heavy_motor', 'Heavy Motor'],
    ['marine_cargo', 'Marine Cargo & Goods in Transit'], ['crop', 'Crop'],
  ];
  const [cls, setCls] = useState('public_liability');
  const CLASS_PACKS = {
    strata: [['st-01', 'Lot & Common Property Schedule'], ['st-02', 'Valuation Currency Declaration'], ['st-03', 'Committee & Management Declaration']],
    farm: [['fm-01', 'Property, Livestock & Crop Declaration'], ['fm-03', 'Fire & Chemical Compliance']],
    plant_equipment: [['pe-01', 'Plant Schedule & Hire Declaration'], ['pe-03', 'Security & Maintenance Attestation']],
    group_personal_accident: [['pa-01', 'Category Schedule & Benefit Election']],
    trade_credit: [['tc-02', 'Buyer Schedule & Credit Limits'], ['tc-03', 'Overdue Notification'], ['tc-04', 'Turnover Declaration']],
    personal_motor: [['pm-01', 'Vehicle, Valuation & Use Declaration']],
    marine_cargo: [['mc-02', 'Open Cover Shipment Declaration']],
    crop: [['cr-01', 'Paddock & Planting Declaration']],
  };
  const MOTOR = ['commercial_motor', 'heavy_motor', 'personal_motor'];
  const FLEET = ['commercial_motor', 'heavy_motor'];
  const isMotor = MOTOR.includes(cls);
  const link = (href, label, testid) => (
    <a key={testid} href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-medium"
      style={{ background: 'rgba(26,76,124,0.06)', color: C.blue, border: '1px solid rgba(26,76,124,0.15)' }}>
      <Download className="w-3.5 h-3.5" />{label}
    </a>
  );
  return (
    <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3" data-testid="forms-library-card">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Forms Library — blank, printable</p>
        <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>Proposal forms per class (with the duty-of-disclosure notice on the face), statutory forms and worksheets.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={cls} onChange={e => setCls(e.target.value)} className="px-3 py-2 text-xs border rounded-sm bg-white" data-testid="forms-class-select">
          {CLASSES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        {link(`${API_BASE}/documents/proposal-form/${cls}.pdf`, 'Blank Proposal Form', 'forms-proposal-btn')}
        {isMotor
          ? (['commercial_motor', 'heavy_motor'].includes(cls)
            ? link(`${API_BASE}/documents/motor-accident-claim.pdf`, 'Motor Accident Claim Form', 'forms-motor-claim-btn')
            : link(`${API_BASE}/documents/motor-claim-form.pdf?class_id=${cls}`, 'Motor Accident Claim Form', 'forms-motor-claim-btn'))
          : link(`${API_BASE}/documents/claim-form.pdf?class_id=${cls}`, 'Claim Form', 'forms-claim-btn')}
        {FLEET.includes(cls) && link(`${API_BASE}/documents/worksheet/fleet-declaration.pdf`, 'Fleet Schedule & Garaging', 'forms-fleet-btn')}
        {FLEET.includes(cls) && link(`${API_BASE}/documents/driver-declaration.pdf`, 'Driver Declaration', 'forms-driver-btn')}
      </div>
      {FLEET.includes(cls) && (
        <div className="flex flex-wrap gap-2">
          {link(`${API_BASE}/documents/vehicle-driver-schedule.pdf`, 'Vehicle & Driver Schedule', 'forms-vehicle-schedule-btn')}
          {link(`${API_BASE}/documents/risk-management-attestation.pdf`, 'Risk Management Attestation', 'forms-risk-attestation-btn')}
          {link(`${API_BASE}/documents/fleet-adjustment.pdf`, 'Fleet Declaration & Adjustment', 'forms-fleet-adjustment-btn')}
          {cls === 'heavy_motor' && link(`${API_BASE}/documents/heavy-vehicle-compliance.pdf`, 'Heavy Vehicle Compliance', 'forms-heavy-compliance-btn')}
        </div>
      )}
      {cls === 'construction' && (
        <div className="flex flex-wrap gap-2" data-testid="forms-construction-pack">
          {link(`${API_BASE}/documents/construction/cn-01.pdf`, 'Project Declaration Form', 'forms-cn01-btn')}
          {link(`${API_BASE}/documents/construction/cn-02.pdf`, 'DBP Statutory Duty Declaration', 'forms-cn02-btn')}
          {link(`${API_BASE}/documents/construction/cn-03.pdf`, 'Existing Structures & Adjoining Property', 'forms-cn03-btn')}
        </div>
      )}
      {CLASS_PACKS[cls] && (
        <div className="flex flex-wrap gap-2" data-testid={`forms-class-pack-${cls}`}>
          {CLASS_PACKS[cls].map(([code, label]) => link(`${API_BASE}/documents/class-forms/${code}.pdf`, label, `forms-${code.replace('-', '')}-btn`))}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {link(`${API_BASE}/documents/alteration-request.pdf`, 'Mid-Term Alteration Request', 'forms-alteration-btn')}
        {link(`${API_BASE}/documents/sbe-declaration.pdf`, 'NSW SBE Duty Exemption Declaration', 'forms-sbe-btn')}
        {link(`${API_BASE}/documents/worksheet/sum-insured.pdf`, 'Sum Insured Worksheet', 'forms-si-worksheet-btn')}
        {link(`${API_BASE}/documents/worksheet/bi.pdf`, 'Business Interruption Worksheet', 'forms-bi-worksheet-btn')}
      </div>
    </Card>
  );
}

function TemplatesTab({ api, isAdmin }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ doc_type: 'policy_schedule', name: '' });
  const [file, setFile] = useState(null);
  const load = useCallback(() => { api.get('/dms/templates').then(r => setData(r.data)).catch(() => {}); }, [api]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file || !form.name) { toast.error('Name and file required'); return; }
    const fd = new FormData();
    fd.append('doc_type', form.doc_type); fd.append('name', form.name); fd.append('file', file);
    try { await api.post('/dms/templates', fd); toast.success('Template uploaded as DRAFT'); setFile(null); setForm({ ...form, name: '' }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };
  const approve = async (id) => {
    try { await api.post(`/dms/templates/${id}/approve`); toast.success('Template APPROVED — prior version superseded'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };

  if (!data) return <Skeleton className="h-32 rounded-sm" />;
  return (
    <div className="space-y-4" data-testid="dms-templates-tab">
      {isAdmin && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Upload template (DRAFT → APPROVED gate)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })} className="px-3 py-2 text-sm border rounded-sm bg-white" data-testid="template-type-select">
              {data.template_types.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
            <Input placeholder="Template name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="template-name-input" />
            <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="template-file-input" />
          </div>
          <button onClick={upload} className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="template-upload-btn">
            <Upload className="w-4 h-4" /> Upload as DRAFT
          </button>
        </Card>
      )}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]"><h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Template Register</h3></div>
        <div className="p-4 space-y-2">
          {data.templates.length === 0 && <p className="text-sm text-center py-4" style={{ color: C.slate }}>No templates yet. Documents are issued from the built-in layouts until an APPROVED template exists. Upload your schedule / endorsement / invoice / cancellation layouts here.</p>}
          {data.templates.map(t => (
            <div key={t.template_id || t.doc_code} className="flex items-center justify-between py-2 border-b border-[rgba(136,150,168,0.08)]" data-testid={`template-row-${t.template_id || t.doc_code}`}>
              <div>
                <span className="text-xs font-medium" style={{ color: C.navy }}>{t.name}</span>
                <span className="text-xs ml-2" style={{ color: C.slate }}>
                  {(t.doc_type || t.doc_code || 'document suite').replace(/_/g, ' ')} · v{t.version || '—'} · {t.uploaded_by || 'system'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] px-2 py-0.5 rounded font-bold" style={{ background: (STATUS_COLORS[t.status] || C.slate) + '22', color: STATUS_COLORS[t.status] || C.slate }}>{t.status}</span>
                {isAdmin && t.status === 'DRAFT' && (
                  <button onClick={() => approve(t.template_id)} className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: C.green }} data-testid={`approve-template-${t.template_id}`}>
                    <CheckCircle className="w-3.5 h-3.5" /> Approve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function IntegrityCard({ api }) {
  const [sweep, setSweep] = useState(null);
  const [busy, setBusy] = useState('');
  const [reason, setReason] = useState('');
  const [showRestamp, setShowRestamp] = useState(false);

  useEffect(() => {
    api.get('/dms/summary').then(r => setSweep(r.data?.last_sweep || null)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSweep = async () => {
    setBusy('sweep');
    try {
      const { data } = await api.post('/dms/integrity-sweep');
      setSweep(data);
      const flagged = (data.mismatches?.length || 0) + (data.missing?.length || 0);
      flagged ? toast.error(`${flagged} document(s) flagged — fingerprint or stored copy`)
              : toast.success(`${data.checked} documents verified — every fingerprint matches`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sweep failed'); }
    finally { setBusy(''); }
  };

  const restamp = async () => {
    setBusy('restamp');
    try {
      const { data } = await api.post('/dms/re-fingerprint', { reason });
      toast.success(`${data.restamped_count} fingerprint(s) replaced — prior hashes kept on record`);
      setReason(''); setShowRestamp(false);
      await runSweep();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Re-fingerprint failed'); }
    finally { setBusy(''); }
  };

  const mismatches = sweep?.mismatches?.length || 0;
  const missing = sweep?.missing?.length || 0;
  const unreadable = sweep?.unreadable?.length || 0;
  const flagged = mismatches + missing;
  const chip = (label, n, color) => (
    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold" data-testid={`dms-integrity-${label.toLowerCase()}`}
      style={{ background: n ? `${color}14` : 'rgba(136,150,168,0.1)', color: n ? color : C.slate, border: `1px solid ${n ? color : 'transparent'}` }}>
      {label}: {n}
    </span>
  );

  return (
    <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3" data-testid="dms-integrity-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Register integrity</p>
          <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>
            Every fingerprint re-checked against the bytes in storage. A stored copy that has vanished is reported, not passed over.
          </p>
        </div>
        <button onClick={runSweep} disabled={!!busy} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-medium text-white disabled:opacity-50"
          style={{ background: C.blue }} data-testid="dms-sweep-btn">
          <ShieldCheck className="w-3.5 h-3.5" /> {busy === 'sweep' ? 'Sweeping…' : 'Run integrity sweep'}
        </button>
      </div>

      {sweep ? (
        <div className="flex flex-wrap items-center gap-2">
          {chip('Checked', sweep.checked || 0, C.green)}
          {chip('Mismatches', mismatches, C.red)}
          {chip('Missing', missing, C.red)}
          {chip('Unreadable', unreadable, C.amber)}
          <span className="text-[11px]" style={{ color: C.slate }} data-testid="dms-sweep-ran-at">
            last run {new Date(sweep.ran_at).toLocaleString('en-AU')}
          </span>
        </div>
      ) : <p className="text-[11px]" style={{ color: C.slate }} data-testid="dms-sweep-never">No sweep on record yet.</p>}

      {flagged > 0 && (
        <div className="space-y-2 p-3 rounded-sm" style={{ background: 'rgba(226,75,74,0.06)', border: `1px solid rgba(226,75,74,0.2)` }}>
          <p className="text-[11px] font-medium" style={{ color: C.red }} data-testid="dms-integrity-flagged">
            {flagged} document(s) flagged. If the content was legitimately replaced, restamp the register — the prior fingerprint stays on the record.
          </p>
          <div className="max-h-28 overflow-auto space-y-0.5">
            {[...(sweep.mismatches || []), ...(sweep.missing || [])].slice(0, 12).map(m => (
              <p key={m.id} className="text-[11px] truncate" style={{ color: C.slate }}>· {m.title}</p>
            ))}
          </div>
          {showRestamp ? (
            <div className="flex flex-wrap gap-2 items-center">
              <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is the fingerprint being replaced? (recorded, min 10 chars)"
                className="flex-1 min-w-[260px] rounded-sm text-xs" data-testid="dms-restamp-reason" />
              <button onClick={restamp} disabled={reason.trim().length < 10 || !!busy}
                className="px-3 py-2 rounded-sm text-xs font-medium text-white disabled:opacity-50" style={{ background: C.red }} data-testid="dms-restamp-btn">
                {busy === 'restamp' ? 'Restamping…' : 'Restamp fingerprints'}
              </button>
              <button onClick={() => { setShowRestamp(false); setReason(''); }} className="text-xs" style={{ color: C.slate }} data-testid="dms-restamp-cancel">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowRestamp(true)} className="text-xs font-medium hover:underline" style={{ color: C.red }} data-testid="dms-restamp-toggle">
              Re-fingerprint from storage…
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

export default function DocumentsPage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['mga_admin', 'platform_owner'].includes(user?.role);
  const isStaff = user?.role !== 'broker';
  const [tab, setTab] = useState('register');
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const search = useCallback((query = q, category = cat) => {
    setLoading(true);
    api.get('/dms/documents', { params: { q: query, category, limit: 50 } })
      .then(r => setResult(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [api, q, cat]);

  useEffect(() => {
    search('', '');
    if (isStaff) api.get('/dms/summary').then(r => setSummary(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (d) => {
    try {
      if (d.storage?.kind === 'ephemeral') {
        const { data } = await api.get(`/dms/documents/${d.document_id}/download`);
        const resp = await api.get(data.regenerate_url.replace('/api', ''), { responseType: 'blob' });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement('a'); a.href = url; a.download = `${d.title.replace(/\s+/g, '_')}.pdf`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const resp = await api.get(`/dms/documents/${d.document_id}/download`, { responseType: 'blob' });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement('a'); a.href = url; a.download = d.title.replace(/\s+/g, '_'); a.click();
        URL.revokeObjectURL(url);
      }
    } catch { toast.error('Download failed'); }
  };

  const archive = async (d) => {
    try { await api.post(`/dms/documents/${d.document_id}/archive`); toast.success('Archived (retained, never destroyed)'); search(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Archive failed'); }
  };

  const downloadMatrix = async () => {
    try {
      const resp = await api.get('/documents/coverage-matrix.xlsx', { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'Document_Coverage_Matrix.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: C.navy, fontFamily: "'IBM Plex Sans'" }}>Documents</h1>
          <p className="text-sm mt-1" style={{ color: C.slate }}>One register for every document — generated, uploaded or product. 7-year retention, fingerprinted, audited.</p>
        </div>
        {isStaff && (
          <button onClick={downloadMatrix} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-medium text-white shrink-0" style={{ background: C.blue }} data-testid="dms-coverage-matrix-btn">
            <Download className="w-3.5 h-3.5" /> Coverage matrix (XLSX)
          </button>
        )}
      </div>

      {isStaff && summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Registered" value={summary.total_active.toLocaleString()} color={C.blue} />
          <KPI label="Product docs" value={summary.by_category?.product || 0} color={C.teal} />
          <KPI label="Transaction docs" value={(summary.by_category?.transaction || 0).toLocaleString()} color={C.green} />
          <KPI label="Uploads" value={summary.by_category?.upload || 0} color={C.amber} />
          <KPI label="Approved templates" value={summary.approved_templates} color={C.navy} />
        </div>
      )}

      {isStaff && (
        <div className="flex gap-2 border-b border-[rgba(136,150,168,0.2)]">
          {[['register', 'Register'], ['templates', 'Templates']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} className="px-4 py-2 text-sm font-medium -mb-px" data-testid={`dms-tab-${id}`}
              style={{ color: tab === id ? C.blue : C.slate, borderBottom: tab === id ? `2px solid ${C.blue}` : '2px solid transparent' }}>{label}</button>
          ))}
        </div>
      )}

      {isStaff && tab === 'register' && isAdmin && <IntegrityCard api={api} />}

      {isStaff && tab === 'register' && <FormsLibraryCard API_BASE={process.env.REACT_APP_BACKEND_URL + '/api'} />}

      {tab === 'templates' && isStaff ? <TemplatesTab api={api} isAdmin={isAdmin} /> : (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-2.5" style={{ color: C.slate }} />
              <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
                placeholder="Search by client, policy number, claim, type..." className="pl-9 rounded-sm" data-testid="dms-search-input" />
            </div>
            {CATS.map(([id, label]) => (
              <button key={id} onClick={() => { setCat(id); search(q, id); }} className="px-3 py-1.5 rounded-full text-xs font-medium border"
                style={{ background: cat === id ? C.blue : '#fff', color: cat === id ? '#fff' : C.slate, borderColor: cat === id ? C.blue : '#cbd5e1' }}
                data-testid={`dms-cat-${id || 'all'}`}>{label}</button>
            ))}
            <button onClick={() => search()} className="px-4 py-2 rounded-sm text-sm font-medium text-white flex items-center gap-1.5" style={{ background: C.blue }} data-testid="dms-search-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Search
            </button>
          </div>

          {loading ? <Skeleton className="h-64 rounded-sm" /> : (
            <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
              <div className="p-3 border-b border-[rgba(136,150,168,0.15)] flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Document Register</h3>
                <span className="text-xs" style={{ color: C.slate }} data-testid="dms-result-count">{result?.total?.toLocaleString() || 0} documents</span>
              </div>
              <div className="divide-y divide-[rgba(136,150,168,0.08)]">
                {(result?.documents || []).map(d => (
                  <div key={d.document_id} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50" data-testid={`dms-row-${d.document_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-4 h-4 shrink-0" style={{ color: C.teal }} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: C.navy }}>{d.title}</p>
                        <p className="text-[11px]" style={{ color: C.slate }}>
                          {d.doc_type.replace(/_/g, ' ')} · {d.category} · {d.business_name || d.refs?.policy_id || d.refs?.claim_id || ''} · {new Date(d.created_at).toLocaleDateString('en-AU')}
                          {d.sha256 && <span className="ml-1.5 inline-flex items-center gap-0.5"><ShieldCheck className="w-3 h-3 inline" style={{ color: C.green }} />fingerprinted</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button onClick={() => download(d)} className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: C.blue }} data-testid={`dms-download-${d.document_id}`}>
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                      {isAdmin && d.status === 'active' && (
                        <button onClick={() => archive(d)} className="flex items-center gap-1 text-xs hover:underline" style={{ color: C.slate }} data-testid={`dms-archive-${d.document_id}`}>
                          <Archive className="w-3.5 h-3.5" /> Archive
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {(result?.documents || []).length === 0 && <p className="text-sm text-center py-8" style={{ color: C.slate }}>No documents match.</p>}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
